import { AgGridVue } from 'ag-grid-vue3';
import { ComponentUtil } from 'ag-grid-community';

/**
 * Patch ag-grid-vue3 to fix a critical upstream concurrency bug:
 * In ag-grid-vue3 (v31.x), property watchers share a single closure-scoped
 * `changes` object and `timeout` across ALL AgGridVue component instances in the app.
 *
 * When two or more grids are on the same page (e.g., ChargesSection and DimensionsSection)
 * and their properties (such as columnDefs) update in the same Vue reactivity tick
 * (for example, when clicking "Modify" which toggles `disabled` on both grids),
 * Grid B's properties overwrite Grid A's properties in the shared `changes` object.
 * When the 0ms timeout fires, Grid A's API is invoked with Grid B's properties,
 * causing Grid A (Charges) to temporarily or permanently display Grid B's (Dimensions) columns!
 *
 * This patch replaces the shared watchers on AgGridVue with per-instance state
 * (`this._agChanges` and `this._agTimeout`), completely isolating each grid instance.
 */
export function applyAgGridVuePatch() {
  if (!AgGridVue || !AgGridVue.watch) return;
  if (AgGridVue.__patchedForSharedWatchers) return;
  AgGridVue.__patchedForSharedWatchers = true;

  const watchKeys = Object.keys(AgGridVue.watch);
  for (const propName of watchKeys) {
    if (propName === 'modelValue') continue;

    AgGridVue.watch[propName] = {
      handler(currentValue) {
        if (!this.gridCreated || !this.api || this.isDestroyed) return;

        if (!this._agChanges) this._agChanges = {};
        this._agChanges[propName] =
          currentValue === ComponentUtil.VUE_OMITTED_PROPERTY ? undefined : currentValue;

        if (this._agTimeout == null) {
          this._agTimeout = setTimeout(() => {
            if (this.api && !this.isDestroyed && this._agChanges) {
              const pendingChanges = this._agChanges;
              this._agChanges = {};
              ComponentUtil.processOnChange(pendingChanges, this.api, true);
            }
            this._agTimeout = null;
          }, 0);
        }
      },
      deep: true,
    };
  }

  // Clean up timer if component unmounts
  const origUnmounted = AgGridVue.unmounted;
  AgGridVue.unmounted = function () {
    if (this._agTimeout != null) {
      clearTimeout(this._agTimeout);
      this._agTimeout = null;
    }
    this._agChanges = null;
    if (origUnmounted) {
      origUnmounted.call(this);
    }
  };
}

// Auto-apply immediately when imported
applyAgGridVuePatch();
